/**
 * Checkout SSC Composable
 * Phase 2.5: SSC Insurance Data
 *
 * Thin wrapper over checkout store SSC state.
 * Provides UI-ready insurance display items.
 * Selection state and price computation live in the store
 * (accessible to bookingService.ts for DaVinci payload).
 */
import { computed } from 'vue';
import { useCheckoutStore } from '@/stores/checkout';
import { getInsuranceTotalPrice, getInsuranceAvailability } from '@/types/schemas/ssc';
import { isHiddenAuxiliaryAvailability } from '@/constants/extras/availability';
import { formatPrice } from '@/utils/formatting/currencyFormatter';

export interface InsuranceDisplayItem {
  id: string;
  title: string;
  description: string;
  price: string;
  priceRaw: number;
  availability: 'available' | 'on-request' | 'unavailable';
  underAccordion: true;
  wrapperClass?: string;
}

export function useCheckoutSsc() {
  const checkout = useCheckoutStore();

  const isLoading = computed(() => checkout.isLoadingSsc);
  const error = computed(() => checkout.sscError);
  const hasData = computed(() => checkout.hasSscData);
  const rawInsurances = computed(() => checkout.sscInsurances);

  const selectedInsuranceId = computed(() => checkout.selectedInsuranceId);
  const selectedInsurance = computed(() => checkout.selectedInsurance);
  const selectedInsurancePrice = computed(() => checkout.selectedInsurancePrice);

  async function fetchData(): Promise<void> {
    await checkout.fetchSscInsurances();
  }

  function selectInsurance(id: string): void {
    checkout.selectInsurance(id);
  }

  const insuranceDisplayItems = computed((): InsuranceDisplayItem[] => {
    const visible = rawInsurances.value.filter((insurance) => {
      const raw = insurance.ServiceTypes[0]?.Availability ?? '';
      return !isHiddenAuxiliaryAvailability(raw);
    });

    return visible.map((insurance, index) => {
      const isLast = index === visible.length - 1;
      const totalPrice = getInsuranceTotalPrice(insurance);
      const availability = getInsuranceAvailability(insurance);

      return {
        id: insurance.BookingCode,
        title: insurance.BookingCodeName,
        description: insurance.ServiceTypes.length > 0 ? insurance.ServiceTypes[0].Code_Name : '',
        price: formatPrice(totalPrice, true),
        priceRaw: totalPrice,
        availability,
        underAccordion: true as const,
        wrapperClass: isLast ? undefined : 'pb-5 mb-5 border-b border-opacity-50',
      };
    });
  });

  return {
    isLoading,
    error,
    hasData,
    rawInsurances,
    selectedInsuranceId,
    selectedInsurance,
    selectedInsurancePrice,
    insuranceDisplayItems,
    fetchData,
    selectInsurance,
  };
}
